import { TFunction } from 'i18next'
import { z } from 'zod'

import { GiftCardModel } from '@/api/gift-cards/get-gift-cards'
import { getPersonColumn, PersonColumnSettings } from '@/components/business/person-column'
import { Badge } from '@/components/ui/badge'
import { DEFAULT_LIMIT } from '@/components/ui/pagination/pagination.constants'
import { StatusTabOption } from '@/components/ui/status-tabs'
import { TableColumn } from '@/components/ui/table'
import { Typography } from '@/components/ui/typography'
import { EMPTY_VALUE } from '@/constants/ui'
import { formatDateTimeInTimezone } from '@/utils'

import * as S from './gift-cards-dashboard.styled'
import {
  getGiftCardCategoryLabel,
  getGiftCardStatusColor,
  getGiftCardStatusLabel,
} from './gift-cards-dashboard.utils'

export const GIFT_CARDS_DASHBOARD_ID = 'gift-cards-dashboard'
export const ADDITIONAL_FILTERS_DROPDOWN_WIDTH = '32.8rem'

export const giftCardsDashboardFiltersSchema = z.object({
  category: z.string().optional(),
  clientId: z.array(z.coerce.number()).optional(),
  limit: z.coerce.number().optional().default(DEFAULT_LIMIT),
  search: z.string().optional().default(''),
  skip: z.coerce.number().optional().default(0),
  status: z.string().optional(),
  type: z.string().optional(),
})

export type GiftCardsDashboardFilters = z.infer<typeof giftCardsDashboardFiltersSchema>

export const initialGiftCardsDashboardFilters: GiftCardsDashboardFilters = {
  category: '',
  clientId: [],
  limit: DEFAULT_LIMIT,
  search: '',
  skip: 0,
  status: '',
  type: '',
}

export const getGiftCardsTypeTabs = (t: TFunction<['gift-cards']>): StatusTabOption<string>[] => [
  { label: t('gift-cards:tabs.all'), value: '' },
  { label: t('gift-cards:tabs.external'), value: 'external' },
  { label: t('gift-cards:tabs.customAmenity'), value: 'amenity' },
  { label: t('gift-cards:tabs.customServices'), value: 'service' },
]

export const getGiftCardStatusOptions = (t: TFunction<['gift-cards']>) => [
  { label: t('gift-cards:statuses.redeemed'), value: 'redeemed' },
  { label: t('gift-cards:statuses.notRedeemed'), value: 'not-redeemed' },
  { label: t('gift-cards:statuses.expired'), value: 'expired' },
]

export const getGiftCardCategoryOptions = (t: TFunction<['gift-cards']>) => [
  { label: t('gift-cards:categories.referral'), value: 'referral' },
  { label: t('gift-cards:categories.moveIn'), value: 'moveIn' },
  { label: t('gift-cards:categories.renewal'), value: 'renewal' },
  { label: t('gift-cards:categories.maintenanceIssue'), value: 'maintenanceIssue' },
  { label: t('gift-cards:categories.other'), value: 'other' },
]

export const getGiftCardsColumns = ({
  t,
  timezone,
  recipientColumnSettings,
}: {
  t: TFunction<['gift-cards']>
  timezone: string
  recipientColumnSettings: PersonColumnSettings
}): TableColumn<GiftCardModel>[] => [
  {
    id: 'sendDate',
    render: (card) => (
      <Typography as="span" size="text-small" color="black800">
        {formatDateTimeInTimezone(card.sendDate, timezone)}
      </Typography>
    ),
    title: t('gift-cards:columns.sendDate'),
    width: '16rem',
  },
  {
    id: 'source',
    render: (card) => {
      if (card.source === 'loyalty') {
        return (
          <Typography as="span" size="text-small" color="black800">
            {t('gift-cards:columns.loyaltySystem')}
          </Typography>
        )
      }
      if (!card.createdByUser) return EMPTY_VALUE
      return (
        <S.CellTextWrapper>
          <Typography as="span" size="text-small" weight="medium" color="black800" breakLines={1}>
            {`${card.createdByUser.firstName} ${card.createdByUser.lastName}`}
          </Typography>
          <Typography as="span" size="text-small" color="black400" breakLines={1}>
            {card.createdByUser.role}
          </Typography>
        </S.CellTextWrapper>
      )
    },
    title: t('gift-cards:columns.source'),
    width: '16rem',
  },
  {
    id: 'giftCardId',
    render: (card) => (
      <Typography as="span" size="text-small" color="black800">
        {card.id}
      </Typography>
    ),
    title: t('gift-cards:columns.giftCardId'),
    width: '12rem',
  },
  {
    id: 'giftCardTypeName',
    render: (card) => (
      <S.CellTextWrapper>
        <Typography
          as="span"
          size="text-small"
          weight="medium"
          color="black800"
          textTransform="capitalize"
          breakLines={1}
        >
          {card.type}
        </Typography>
        <Typography as="span" size="text-small" color="black400" breakLines={1}>
          {card.name}
        </Typography>
      </S.CellTextWrapper>
    ),
    title: t('gift-cards:columns.giftCardTypeName'),
    width: '18rem',
  },
  {
    id: 'category',
    render: (card) => getGiftCardCategoryLabel(card.category, t),
    title: t('gift-cards:columns.category'),
    width: '16rem',
  },
  {
    id: 'amount',
    render: (card) => `$${card.amount}`,
    title: t('gift-cards:columns.value'),
    width: '10rem',
  },
  {
    id: 'status',
    render: (card) => (
      <Badge color={getGiftCardStatusColor(card.status)} variant="light" size="small">
        {getGiftCardStatusLabel(card.status, t)}
      </Badge>
    ),
    title: t('gift-cards:columns.status'),
    width: '14rem',
  },
  {
    id: 'redeemDate',
    render: (card) =>
      card.redeemDate ? (
        <Typography as="span" size="text-small" color="black800">
          {formatDateTimeInTimezone(card.redeemDate, timezone)}
        </Typography>
      ) : (
        EMPTY_VALUE
      ),
    title: t('gift-cards:columns.redeemDate'),
    width: '16rem',
  },
  getPersonColumn(recipientColumnSettings, {
    getDisplay: (row) => row.recipientDisplay,
    // Note: a gift card can go to someone who isn't one of our residents (no `client`) - still
    // shown with whatever the card has, just without the profile link.
    getPerson: (card) =>
      card.recipientFirstName || card.recipientName
        ? {
            apartment: card.client?.apartment,
            avatar: card.client?.user?.avatar ?? card.client?.avatar,
            buildingNumber: card.client?.buildingNumber,
            clientId: card.client?.id,
            email: card.recipientEmail,
            firstName: card.recipientFirstName ?? card.recipientName,
            kind: 'resident',
            lastName: card.recipientFirstName ? card.recipientLastName : null,
            phone: card.recipientPhone,
            street: card.client?.street,
          }
        : null,
    id: 'recipient',
    pageName: t('gift-cards:pageTitle'),
    source: 'gift-cards',
    title: t('gift-cards:columns.recipient'),
  }),
  {
    id: 'expirationDate',
    render: (card) =>
      card.expirationDate ? (
        <Typography as="span" size="text-small" color="black800">
          {formatDateTimeInTimezone(card.expirationDate, timezone)}
        </Typography>
      ) : (
        EMPTY_VALUE
      ),
    title: t('gift-cards:columns.expirationDate'),
    width: '16rem',
  },
]
